import { BadgeWithTip, type BadgeProps, type BadgeWithTipProps } from "@/components/Badge.tsx";
import { Trans } from "@lingui/react/macro";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import type { ReactNode } from "react";

export interface EnumProps {
    label: ReactNode;
    description?: ReactNode;
    color: BadgeProps["color"];
}

export interface EnumBadgeProps<T extends PropertyKey> extends BadgeWithTipProps {
    options: Record<T, EnumProps>;
    value: T;
}

const unknownEnumProps: EnumProps = {
    label: <Trans comment="Label for an unknown item in a select control">Unknown</Trans>,
    color: "error",
};

function getEnumProps<T extends PropertyKey>(options: Record<T, EnumProps>, value: T): EnumProps {
    return getOwn(options, value) ?? unknownEnumProps;
}

export function EnumBadge<T extends PropertyKey>({ options, value, ...props }: EnumBadgeProps<T>) {
    const { label, description, color } = getEnumProps(options, value);
    return <BadgeWithTip {...props} description={description} label={label} color={color} />;
}

// eslint-disable-next-line react-refresh/only-export-components
export function withEnumBadge<T extends PropertyKey>(options: Record<T, EnumProps>) {
    return function EnumBadgeWithOptions(props: Omit<EnumBadgeProps<T>, "options">) {
        return <EnumBadge {...props} options={options} />;
    };
}
